import { omitBy } from 'lodash-es';

import {
  METADATA_SCAN_FULL,
  getIndexes,
  getPropertyKeysForLabels,
  getPropertyKeysForRelationshipTypes,
  getRareLabels,
  getSchema,
} from '../../services/metagraph';
import type { UpdatedMetadata } from '../../services/perspectives/types';
import { getIndexedAndNonIndexedLabels, getRelevantRelTypes } from '../../state/perspectives/helpers';
import type { AddingIndex } from '../../state/perspectives/perspectiveMetadata.types';
import type { Database } from '../../types/database';
import type { GeneralPropertyKey, PathSegment } from '../../types/perspective';
import { PERSPECTIVE_TYPE } from '../../types/perspective';
import type { Nullable } from '../../types/utility';

type PropertyKeysMap = Record<string, GeneralPropertyKey[]>;
interface LabelStat {
  label: string;
  count: number;
}
interface Metadata {
  labels: string[];
  relationshipTypes: string[];
}

export const constructTypePropertyKeys = (typePropertyKeyList: GeneralPropertyKey[]): PropertyKeysMap => {
  const typePropKeys: PropertyKeysMap = {};
  typePropertyKeyList.reduce((propKeyMap, propKey) => {
    if (!propKeyMap[propKey.type]) {
      propKeyMap[propKey.type] = [];
    }
    propKeyMap[propKey.type]?.push(propKey);
    return propKeyMap;
  }, typePropKeys);
  return typePropKeys;
};

const addLabelPropsToIndexes = (propertyKeysMap: PropertyKeysMap, labels: string[]): AddingIndex[] =>
  Object.keys(propertyKeysMap).reduce((indexesArray: AddingIndex[], label: string) => {
    if (labels.includes(label)) {
      indexesArray.push({
        label,
        type: 'native',
        isMetadataPropIndex: true,
        propertyKeys: propertyKeysMap[label]?.map((property: GeneralPropertyKey) => property.propertyKey) ?? [],
      });
    }
    return indexesArray;
  }, []);

export async function getMetadataForApp(
  serverVersion: string,
  database: Database,
  scanMode: string,
  parentRequestId: string,
  allLabelsAndRelationshipsTypes: Metadata,
  perspectiveType: PERSPECTIVE_TYPE,
  maxLimitForLabelsAndRelTypes: number,
): Promise<UpdatedMetadata> {
  const schemaPromise = await getSchema({
    database,
    serverVersion,
    parentRequestId,
  });
  const indexes: AddingIndex[] = await getIndexes({
    database,
    parentRequestId,
  });

  const [pathSegments, indexesResult] = await Promise.all([schemaPromise, indexes]);
  if (perspectiveType === PERSPECTIVE_TYPE.BLANK) {
    return getBlankPerspectivePropertyKeysForLabelsAndReltypes(
      allLabelsAndRelationshipsTypes,
      pathSegments,
      indexesResult,
    );
  }
  return getAllPerspectivePropertyKeysForLabelsAndReltypes(
    allLabelsAndRelationshipsTypes,
    database,
    scanMode ?? METADATA_SCAN_FULL,
    parentRequestId ?? '',
    pathSegments,
    indexesResult,
    maxLimitForLabelsAndRelTypes,
  );
}

const getBlankPerspectivePropertyKeysForLabelsAndReltypes = async (
  allLabelsAndRelationshipsTypes: Metadata,
  pathSegments: Nullable<PathSegment[]>,
  indexes: AddingIndex[],
) => {
  const propertyKeysForLabelsMap: PropertyKeysMap = {};
  const propertyKeysForRelationshipsTypesMap: PropertyKeysMap = {};
  const { labels: allLabels, relationshipTypes: allRelTypes } = allLabelsAndRelationshipsTypes;
  allLabels.forEach((label) => {
    propertyKeysForLabelsMap[label] = propertyKeysForLabelsMap[label] ?? [];
  });

  allRelTypes.forEach((relType) => {
    propertyKeysForRelationshipsTypesMap[relType] = propertyKeysForRelationshipsTypesMap[relType] ?? [];
  });

  return {
    labelsMap: propertyKeysForLabelsMap,
    relPropsMap: propertyKeysForRelationshipsTypesMap,
    labels: allLabels,
    relTypes: allRelTypes,
    pathSegments,
    indexes,
    metadataPropsIndexes: [],
  };
};

const getAllPerspectivePropertyKeysForLabelsAndReltypes = async (
  allLabelsAndRelationshipsTypes: Metadata,
  database: Database,
  scanMode: string = METADATA_SCAN_FULL,
  parentRequestId: string,
  pathSegments: Nullable<PathSegment[]>,
  indexes: AddingIndex[],
  maxLimitForLabelsAndRelTypes: number,
) => {
  let filteredLabels: string[] = [];
  let filteredRelTypes: string[] = [];

  const { labels: allLabels, relationshipTypes: allRelTypes } = allLabelsAndRelationshipsTypes;

  if (allLabels.length >= maxLimitForLabelsAndRelTypes) {
    filteredLabels = getIndexedAndNonIndexedLabels(allLabels, indexes, maxLimitForLabelsAndRelTypes);
  }
  const filteredOrAllLabels = filteredLabels.length > 0 ? filteredLabels : allLabels;

  if (allRelTypes.length >= maxLimitForLabelsAndRelTypes) {
    filteredRelTypes = getRelevantRelTypes(pathSegments ?? [], maxLimitForLabelsAndRelTypes, filteredOrAllLabels);
  }
  const filteredOrAllRelTypes = filteredRelTypes?.length > 0 ? filteredRelTypes : allRelTypes;

  const propertyKeysPromise: Promise<PropertyKeysMap> = getPropertyKeysForLabels({
    database,
    labels: filteredOrAllLabels,
    scanMode,
    parentRequestId,
  }).then(constructTypePropertyKeys);

  const propertyKeysForRelationshipTypesPromise: Promise<PropertyKeysMap> = getPropertyKeysForRelationshipTypes({
    database,
    relationshipTypes: filteredOrAllRelTypes,
    labels: filteredOrAllLabels,
    scanMode,
    parentRequestId,
  }).then(constructTypePropertyKeys);

  const rareLabelsPromise: Promise<string[]> = getRareLabels({
    labels: filteredOrAllLabels,
    database,
    parentRequestId,
  }).then((labelStats: LabelStat[]) =>
    labelStats.filter((labelStat) => labelStat.count < 1000).map((labelStat) => labelStat.label),
  );

  const result = await Promise.all([propertyKeysPromise, propertyKeysForRelationshipTypesPromise, rareLabelsPromise]);

  const [propertyKeysForLabelsMapRaw, propertyKeysForRelationshipsTypesMap, rareLabels] = result;
  const propertyKeysForLabelsMap = omitBy(propertyKeysForLabelsMapRaw, (_, key) => !filteredOrAllLabels.includes(key));
  let metadataPropsIndexes: AddingIndex[];

  if (propertyKeysForLabelsMap) {
    metadataPropsIndexes = addLabelPropsToIndexes(propertyKeysForLabelsMap, rareLabels);
  } else {
    const rareLabelsProperties: GeneralPropertyKey[] = await getPropertyKeysForLabels({
      labels: rareLabels,
      database,
      parentRequestId,
    });
    metadataPropsIndexes = addLabelPropsToIndexes(constructTypePropertyKeys(rareLabelsProperties), rareLabels);
  }
  allLabels.forEach((label) => {
    propertyKeysForLabelsMap[label] = propertyKeysForLabelsMap[label] ?? [];
  });

  allRelTypes.forEach((relType) => {
    propertyKeysForRelationshipsTypesMap[relType] = propertyKeysForRelationshipsTypesMap[relType] ?? [];
  });

  return {
    labelsMap: propertyKeysForLabelsMap,
    relPropsMap: propertyKeysForRelationshipsTypesMap,
    labels: allLabelsAndRelationshipsTypes.labels,
    relTypes: allLabelsAndRelationshipsTypes.relationshipTypes,
    pathSegments,
    indexes,
    metadataPropsIndexes,
    limitedLabels: filteredLabels,
    limitedRelTypes: filteredRelTypes,
  };
};
